import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  UserPlus, Search, Phone, Mail, MapPin, Stethoscope, 
  MoreVertical, Edit2, Trash2, MessageCircle, History,
  CheckCircle, XCircle, Plus, X, Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';
import { toast } from 'sonner';
import SendMessageModal from '@/components/SendMessageModal';

const SPECIALTIES = [
  'General Practice', 'Cardiology', 'Orthopedics', 'Pediatrics', 
  'Gynecology', 'Neurology', 'Dermatology', 'ENT', 'Ophthalmology',
  'Gastroenterology', 'Pulmonology', 'Nephrology', 'Urology', 'Oncology',
  'Psychiatry', 'Endocrinology', 'Rheumatology', 'Other'
];

export default function ReferringDoctors() {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [messageHistory, setMessageHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    full_name: '',
    specialty: '',
    clinic_address: '',
    phone_number: '',
    email: '',
    status: 'active',
    notes: ''
  });
  
  // Custom message state
  const [customMessage, setCustomMessage] = useState('');
  
  // Template-based message modal state
  const [showTemplateMessageModal, setShowTemplateMessageModal] = useState(false);
  const [templateMessageRecipient, setTemplateMessageRecipient] = useState(null);

  useEffect(() => {
    fetchDoctors();
  }, [statusFilter]);

  const fetchDoctors = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await api.get(`/referring-doctors?${params.toString()}`);
      setDoctors(response.data);
    } catch (error) {
      toast.error('Failed to fetch referring doctors');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    // Debounce search
    setTimeout(() => fetchDoctors(), 300);
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      specialty: '',
      clinic_address: '',
      phone_number: '',
      email: '',
      status: 'active',
      notes: ''
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await api.post('/referring-doctors', formData);
      toast.success('Referring doctor added successfully');
      setShowCreateModal(false);
      resetForm();
      fetchDoctors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add doctor');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedDoctor) return;
    setSaving(true);
    
    try {
      await api.put(`/referring-doctors/${selectedDoctor.id}`, formData);
      toast.success('Doctor updated successfully');
      setShowEditModal(false);
      resetForm();
      fetchDoctors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update doctor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (doctor) => {
    if (!confirm(`Are you sure you want to deactivate ${doctor.full_name}?`)) return;
    
    try {
      await api.delete(`/referring-doctors/${doctor.id}`);
      toast.success('Doctor deactivated successfully');
      fetchDoctors();
    } catch (error) {
      toast.error('Failed to deactivate doctor');
    }
  };

  const openEditModal = (doctor) => {
    setSelectedDoctor(doctor);
    setFormData({
      full_name: doctor.full_name,
      specialty: doctor.specialty || '',
      clinic_address: doctor.clinic_address || '',
      phone_number: doctor.phone_number,
      email: doctor.email || '',
      status: doctor.status,
      notes: doctor.notes || ''
    });
    setShowEditModal(true);
  };

  const openMessageModal = (doctor) => {
    setSelectedDoctor(doctor);
    setCustomMessage('');
    setShowMessageModal(true);
  };

  const openTemplateMessageModal = (doctor) => {
    setTemplateMessageRecipient(doctor);
    setShowTemplateMessageModal(true);
  };

  const openHistoryModal = async (doctor) => {
    setSelectedDoctor(doctor);
    setShowHistoryModal(true);
    
    try {
      const response = await api.get(`/referring-doctors/${doctor.id}/messages`);
      setMessageHistory(response.data);
    } catch (error) {
      toast.error('Failed to fetch message history');
    }
  };

  const sendWhatsAppMessage = async () => {
    if (!selectedDoctor) return;
    setSaving(true);
    
    try {
      const response = await api.post(`/referring-doctors/${selectedDoctor.id}/send-message`, {
        referring_doctor_id: selectedDoctor.id,
        custom_message: customMessage || undefined,
        template_type: customMessage ? 'custom' : 'referral_thanks'
      });
      
      if (response.data.success) {
        toast.success('WhatsApp message sent successfully');
        setShowMessageModal(false);
      } else {
        toast.error(response.data.message || 'Failed to send message');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSaving(false);
    }
  };

  const filteredDoctors = doctors.filter(doctor => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      doctor.full_name?.toLowerCase().includes(query) ||
      doctor.specialty?.toLowerCase().includes(query) ||
      doctor.phone_number?.includes(query)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Referring Doctors</h1>
          <p className="text-slate-600">Manage external medical professionals who refer patients</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowCreateModal(true); }}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="add-referring-doctor-btn"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Add Doctor
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by name, specialty, or phone..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 bg-slate-700/50 border-slate-600 text-white"
                data-testid="search-referring-doctors"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] bg-slate-700/50 border-slate-600 text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border-emerald-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-700 text-sm">Total Doctors</p>
                <p className="text-2xl font-bold text-white">{doctors.length}</p>
              </div>
              <Stethoscope className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-700 text-sm">Active</p>
                <p className="text-2xl font-bold text-white">
                  {doctors.filter(d => d.status === 'active').length}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border-purple-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-700 text-sm">Total Referrals</p>
                <p className="text-2xl font-bold text-white">
                  {doctors.reduce((sum, d) => sum + (d.referral_count || 0), 0)}
                </p>
              </div>
              <UserPlus className="h-8 w-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Doctors List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
        </div>
      ) : filteredDoctors.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-12 text-center">
            <Stethoscope className="h-12 w-12 text-slate-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Referring Doctors Found</h3>
            <p className="text-slate-300 mb-4">Add your first referring doctor to get started</p>
            <Button onClick={() => setShowCreateModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Doctor
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDoctors.map((doctor) => (
            <motion.div
              key={doctor.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-lg">
                        {doctor.full_name?.charAt(0) || 'D'}
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{doctor.full_name}</h3>
                        <p className="text-sm text-slate-300">{doctor.specialty || 'General'}</p>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                        <DropdownMenuItem onClick={() => openEditModal(doctor)} className="text-white hover:bg-slate-700">
                          <Edit2 className="h-4 w-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openTemplateMessageModal(doctor)} className="text-emerald-400 hover:bg-slate-700">
                          <Send className="h-4 w-4 mr-2" /> Send Message (Template)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openMessageModal(doctor)} className="text-white hover:bg-slate-700">
                          <MessageCircle className="h-4 w-4 mr-2" /> Quick WhatsApp
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openHistoryModal(doctor)} className="text-white hover:bg-slate-700">
                          <History className="h-4 w-4 mr-2" /> Message History
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(doctor)} className="text-red-400 hover:bg-slate-700">
                          <Trash2 className="h-4 w-4 mr-2" /> Deactivate
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-300">
                      <Phone className="h-4 w-4 text-slate-500" />
                      {doctor.phone_number}
                    </div>
                    {doctor.email && (
                      <div className="flex items-center gap-2 text-slate-300">
                        <Mail className="h-4 w-4 text-slate-500" />
                        {doctor.email}
                      </div>
                    )}
                    {doctor.clinic_address && (
                      <div className="flex items-start gap-2 text-slate-300">
                        <MapPin className="h-4 w-4 text-slate-500 mt-0.5" />
                        <span className="line-clamp-2">{doctor.clinic_address}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-700">
                    <div className="flex items-center gap-2">
                      <Badge variant={doctor.status === 'active' ? 'default' : 'secondary'} 
                        className={doctor.status === 'active' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-600 text-slate-300'}>
                        {doctor.status}
                      </Badge>
                      <span className="text-xs text-slate-700">
                        {doctor.referral_count || 0} referrals
                      </span>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => openTemplateMessageModal(doctor)}
                      className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
                      data-testid={`send-message-btn-${doctor.id}`}
                    >
                      <Send className="h-3 w-3 mr-1" />
                      Send Message
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => {
        if (!open) { setShowCreateModal(false); setShowEditModal(false); }
      }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stethoscope className="h-5 w-5 text-emerald-400" />
              {showEditModal ? 'Edit Referring Doctor' : 'Add Referring Doctor'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={showEditModal ? handleEdit : handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-slate-300">Full Name *</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  className="bg-slate-700/50 border-slate-600 text-white"
                  placeholder="Dr. John Smith"
                  required
                  data-testid="doctor-name-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">Specialty</Label>
                <Select value={formData.specialty} onValueChange={(v) => setFormData({...formData, specialty: v})}>
                  <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                    <SelectValue placeholder="Select specialty" />
                  </SelectTrigger>
                  <SelectContent>
                    {SPECIALTIES.map(spec => (
                      <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Phone Number (WhatsApp) *</Label>
                <Input
                  value={formData.phone_number}
                  onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
                  className="bg-slate-700/50 border-slate-600 text-white"
                  placeholder="+91 9876543210"
                  required
                  data-testid="doctor-phone-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="bg-slate-700/50 border-slate-600 text-white"
                  placeholder="doctor@clinic.com"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-slate-300">Clinic Address</Label>
                <Textarea
                  value={formData.clinic_address}
                  onChange={(e) => setFormData({...formData, clinic_address: e.target.value})}
                  className="bg-slate-700/50 border-slate-600 text-white"
                  placeholder="Clinic name, street, city..."
                  rows={2}
                />
              </div>
              <div className="col-span-2">
                <Label className="text-slate-300">Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="bg-slate-700/50 border-slate-600 text-white"
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowCreateModal(false); setShowEditModal(false); }}
                className="border-slate-600 text-slate-300">
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={saving}>
                {saving ? 'Saving...' : (showEditModal ? 'Update' : 'Add Doctor')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send Message Modal */}
      <Dialog open={showMessageModal} onOpenChange={setShowMessageModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-emerald-400" />
              Send WhatsApp Message
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedDoctor && (
              <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold">
                  {selectedDoctor.full_name?.charAt(0)}
                </div>
                <div>
                  <p className="font-medium text-white">{selectedDoctor.full_name}</p>
                  <p className="text-sm text-slate-400">{selectedDoctor.phone_number}</p>
                </div>
              </div>
            )}
            <div>
              <Label className="text-slate-300">Custom Message (optional)</Label>
              <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white mt-2"
                placeholder="Leave empty to use default template..."
                rows={4}
              />
              <p className="text-xs text-slate-500 mt-1">
                If empty, the default "Referral Thank You" template will be used.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageModal(false)}
              className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={sendWhatsAppMessage} className="bg-emerald-600 hover:bg-emerald-700" disabled={saving}>
              <Send className="h-4 w-4 mr-2" />
              {saving ? 'Sending...' : 'Send Message'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-400" />
              Message History - {selectedDoctor?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {messageHistory.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No messages sent yet</p>
              </div>
            ) : (
              messageHistory.map((msg) => (
                <div key={msg.id} className="p-3 bg-slate-700/50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant={msg.status === 'sent' ? 'default' : 'destructive'}
                      className={msg.status === 'sent' ? 'bg-emerald-500/20 text-emerald-300' : ''}>
                      {msg.status}
                    </Badge>
                    <span className="text-xs text-slate-400">
                      {new Date(msg.sent_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-slate-300">{msg.message_content}</p>
                  {msg.error_message && (
                    <p className="text-xs text-red-400 mt-1">{msg.error_message}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Template-based Send Message Modal */}
      <SendMessageModal
        open={showTemplateMessageModal}
        onClose={() => {
          setShowTemplateMessageModal(false);
          setTemplateMessageRecipient(null);
        }}
        recipient={templateMessageRecipient}
        recipientType="referring_doctor"
      />
    </div>
  );
}
